import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';
import { client } from '../../api/client';

const galleryAdapter = createEntityAdapter({
    selectId: image => image.ga_id
});

const initialState = {
    byPoi: galleryAdapter.getInitialState({
        status: 'idle',
        error: null
    }),
    all: galleryAdapter.getInitialState({
        status: 'idle',
        error: null
    }),
    byUser: galleryAdapter.getInitialState({
        status: 'idle',
        error: null
    }),
    newPhoto: {
        status: 'idle',
        error: null
    },
    delete: {
        status: 'idle',
        error: null
    },
    imagesCount: null,
    loadingMore: false
};

export const fetchGallery = createAsyncThunk('gallery/fetchGallery', async (poiId) => {
    const response = await client.get('/get_gallery/' + poiId);
    return response;
});

export const fetchLatestPhotos = createAsyncThunk('gallery/fetchLatestPhotos', async ({limit = 20, buffer = 0}) => {
    const response = await client.get(`/get_images/0/${limit}/${buffer}`);
    return response;
});

export const addPhoto = createAsyncThunk('gallery/addPhoto', async ({userId, poiId, photo}) => {
    const response = await client.upload(`/send_photo/${userId}/${poiId}`, {file_name: photo});
    return response;
});

export const fetchUserPhotos = createAsyncThunk('gallery/fetchUserPhotos', async (cardNumber) => {
    const response = await client.get(`/get_images/${cardNumber}`);
    return response;
});

export const deletePhoto = createAsyncThunk('gallery/deletePhoto', async(photoId) => {
    const response = await client.get(`/delete_photo/${photoId}`);
    return response;
});

export const fetchCountImages = createAsyncThunk('reviews/fetchCountImages', async() => {
    const response = await client.get('/getCountImages');
    return response;
});

const gallerySlice = createSlice({
    name: 'gallery',
    initialState,
    reducers: {
    },
    extraReducers: {
        [fetchGallery.pending]: (state, action) => {
            state.byPoi.status = 'loading';
        },
        [fetchGallery.fulfilled]: (state, action) => {
            state.byPoi.status = 'succeeded';
            galleryAdapter.setAll(state.byPoi, action.payload);
        },
        [fetchGallery.rejected]: (state, action) => {
            state.byPoi.status = 'failed';
            state.byPoi.error = action.payload;
        },
        [fetchLatestPhotos.pending]: (state, action) => {
            if (!action.meta.arg.buffer) {
                state.all.status = 'loading';
            } else {
                state.loadingMore = true;
            }
        },
        [fetchLatestPhotos.fulfilled]: (state, action) => {
            state.all.status = 'succeeded';
            if (!action.meta.arg.buffer) {
                galleryAdapter.setAll(state.all, action.payload);
            } else {
                galleryAdapter.upsertMany(state.all, action.payload);
                state.loadingMore = false;
            }
        },
        [fetchLatestPhotos.rejected]: (state, action) => {
            state.all.status = 'failed';
            state.all.error = action.payload;
        },
        [addPhoto.pending]: (state, action) => {
            state.newPhoto.status = 'loading';
        },
        [addPhoto.fulfilled]: (state, action) => {
            state.newPhoto.status = 'succeeded';
        },
        [addPhoto.rejected]: (state, action) => {
            state.newPhoto.status = 'failed';
            state.newPhoto.error = action.payload;
        },
        [fetchUserPhotos.pending]: (state, action) => {
            state.byUser.status = 'loading';
        },
        [fetchUserPhotos.fulfilled]: (state, action) => {
            state.byUser.status = 'succeeded';
            galleryAdapter.setAll(state.byUser, action.payload);
        },
        [fetchUserPhotos.rejected]: (state, action) => {
            state.byUser.status = 'failed';
            state.byUser.error = action.payload;
        },
        [deletePhoto.pending]: (state, action) => {
            state.delete.status = 'loading';
        },
        [deletePhoto.fulfilled]: (state, action) => {
            state.delete.status = 'succeeded';
            state.byUser.status = 'idle';
            state.all.status = 'idle';
        },
        [deletePhoto.rejected]: (state, action) => {
            state.delete.status = 'failed';
            state.delete.error = action.payload;
        },
        [fetchCountImages.fulfilled]: (state, action) => {
            state.imagesCount = action.payload;
        }
    }
});

export default gallerySlice.reducer;

export const {
    selectAll: selectGallery,
    selectById: selectPoiImageById,
    selectIds: selectPoiImageIds
} = galleryAdapter.getSelectors(state => state.gallery.byPoi);

export const {
    selectAll: selectAllPhotos,
    selectById: selectImageById,
    selectIds: selectImageIds
} = galleryAdapter.getSelectors(state => state.gallery.all);

export const {
    selectAll: selectUserPhotos,
    selectById: selectUserPhotoById,
    selectIds: selectUserPhotoIds
} = galleryAdapter.getSelectors(state => state.gallery.byUser);

